import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { goToApp } from '../utils/goToApp';
import StaticBlob from '../utils/StaticBlob';

const ONBOARDING_FLAG = 'steadily_needs_onboarding';

function ConfirmEmailScreen({ apiUrl, onLogin }) {
  const [status, setStatus] = useState('confirming'); // 'confirming' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      setErrorMsg('Invalid confirmation link.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetchWithRetry(`${apiUrl}/api/auth/confirm-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (cancelled) return;

        if (!res.ok) {
          setStatus('error');
          setErrorMsg(data.error || 'Confirmation failed.');
          return;
        }

        localStorage.setItem('token', data.token);
        localStorage.setItem(ONBOARDING_FLAG, '1');

        if (onLogin) {
          await onLogin(data.token);
        }

        setStatus('success');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err.message || 'Network error. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, onLogin]);

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)] flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-500">
      <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden">
        <StaticBlob color="var(--color-accent-warm)" tintAlpha={0.35} className="absolute top-20 left-10 w-72 h-72" />
        <StaticBlob color="var(--color-ink-light)" tintAlpha={0.35} className="absolute bottom-20 right-10 w-96 h-96" />
      </div>

      <div
        className="glass-morphism rounded-3xl shadow-2xl p-10 w-full max-w-md relative z-10 bg-[var(--color-paper-warm)] border border-[var(--color-ink-subtle)]/10 animate-slideUp text-center"
        style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
      >
        <div className="inline-flex p-4 rounded-3xl mb-6">
          <img
            src={process.env.PUBLIC_URL + '/android-chrome-192x192.png'}
            alt="Steadily.me logo"
            className="w-16 h-16 opacity-90 contrast-125 filter sepia-[.25]"
          />
        </div>
        <h1 className="text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-6">
          Steadily.me
        </h1>

        {status === 'confirming' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[var(--color-accent-warm)] animate-spin mb-3" />
            <p className="text-[var(--color-ink-light)] text-base font-serif">Confirming your email...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto text-green-600 mb-3" />
            <p className="text-green-700 text-xl font-serif font-bold mb-3">Your email is confirmed</p>
            <p className="text-[var(--color-ink-light)] text-base font-serif mb-2">
              You can head back to the Steadily app now. It will pick this up on its own.
            </p>
            <p className="text-[var(--color-ink-light)] text-sm font-serif italic mb-8">
              You can close this tab.
            </p>
            <button
              type="button"
              onClick={() => goToApp(navigate)}
              className="text-[var(--color-ink-light)] text-sm font-serif underline underline-offset-4 hover:text-[var(--color-ink-main)] transition-colors"
            >
              Or open Steadily in this browser
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 mb-3" />
            <p className="text-red-600 text-base font-serif mb-2">{errorMsg}</p>
            <p className="text-[var(--color-ink-light)] text-sm font-serif italic mb-6">
              Your confirmation link may have expired or already been used. You can
              send yourself a new one from the Steadily app.
            </p>
            <a
              href="/login"
              className="inline-block py-3 px-6 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] font-bold font-serif hover:bg-[var(--color-ink-light)] transition-all shadow-md"
              style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
            >
              Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}

export default ConfirmEmailScreen;
